import { useAnimationFrame, useReducedMotion } from 'framer-motion'
import { useRef, type RefObject } from 'react'

/*
 * The walker from documentation/evtimia-walker (wave and point poses), drawn
 * inline so its right arm can be computed each frame: it waves, swings round
 * to point at the hero buttons, holds, and waves again.
 *
 * Where the buttons are depends on the layout: to the left on wide screens,
 * above on phones, where the walker stacks under the text. So the pointing
 * angle is measured from the real positions.
 *
 * The drawing is always mirrored, so the moving arm is the one on the
 * viewer's left: it is nearer the buttons in every layout, and it is the only
 * arm that should ever go up. Choosing the side from the buttons' position
 * made the phone layout flick between arms, as the buttons sit almost
 * directly above the walker there.
 */

const deg = Math.PI / 180

// Arm geometry, read off the two pose SVGs (viewBox -320 -330 640 650).
const WAVE = { shoulderY: 0, length: 136, angle: -42.6 * deg, bend: 27 * deg, ctrl: 0.72, hand: 22 }
const POINT = { shoulderY: 10, length: 155, bend: 2.4 * deg, ctrl: 0.68, hand: 18 }
const SHOULDER_X = 130

/*
 * The two pairs of eyes, also from the pose SVGs: big and round while waving,
 * narrower and turned towards the buttons while pointing. Each eye is a pill
 * (a rect with fully rounded ends) centred on its own origin, so the shape
 * can be morphed by interpolating size, position and tilt.
 */
const EYES = [
  { wave: { w: 66, h: 94, x: -32, y: 14, r: 9 }, point: { w: 42, h: 76, x: -4, y: 29, r: -19 } },
  { wave: { w: 64, h: 92, x: 70, y: 14, r: 9 }, point: { w: 32, h: 58, x: 101, y: 4, r: -18 } },
]

// One cycle, in seconds: wave, swing to point, hold, swing back.
const T_WAVE = 2.4
const T_REACH = 0.6
const T_HOLD = 5
const CYCLE = T_WAVE + T_REACH + T_HOLD + T_REACH

const lerp = (a: number, b: number, t: number) => a + (b - a) * t
/** Smoothstep: starts and lands without a jolt, and never overshoots. */
const ease = (t: number) => t * t * (3 - 2 * t)
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

export function HeroWalker({ targetRef, className }: { targetRef: RefObject<HTMLElement>; className?: string }) {
  const reduceMotion = useReducedMotion()
  const svgRef = useRef<SVGSVGElement>(null)
  const armRef = useRef<SVGPathElement>(null)
  const handRef = useRef<SVGCircleElement>(null)
  const eyeRefs = useRef<(SVGRectElement | null)[]>([])
  const pointAngle = useRef(-7 * deg)
  const lastMeasure = useRef(-Infinity)

  // Re-measured twice a second rather than on resize alone: the buttons and
  // the walker both slide in on load, which no resize event reports.
  const measure = () => {
    const svg = svgRef.current
    const target = targetRef.current
    if (!svg || !target) return
    const s = svg.getBoundingClientRect()
    if (!s.width) return
    // The buttons themselves, not their row: on phones the row is full width,
    // so its centre would sit over the walker rather than over the buttons.
    const rects = [...target.children].map(c => c.getBoundingClientRect())
    if (!rects.length) return
    const left = Math.min(...rects.map(r => r.left))
    const right = Math.max(...rects.map(r => r.right))
    const top = Math.min(...rects.map(r => r.top))
    const bottom = Math.max(...rects.map(r => r.bottom))
    const scale = s.width / 640
    // The pointing shoulder on screen; the drawing is mirrored, so it is the
    // left one.
    const shoulderX = s.left + (320 - SHOULDER_X) * scale
    const shoulderY = s.top + (330 + POINT.shoulderY) * scale
    const dx = shoulderX - (left + right) / 2
    const dy = (top + bottom) / 2 - shoulderY
    // Straight up would hide the arm behind the head; straight down looks limp.
    pointAngle.current = clamp(Math.atan2(dy, dx), -80 * deg, 40 * deg)
  }

  const draw = (blend: number, wobble: number, reach: number) => {
    const arm = armRef.current
    const hand = handRef.current
    if (!arm || !hand) return
    const pa = pointAngle.current
    // Pointing upwards, bow the elbow outwards so the arm clears the body.
    const pointBend = POINT.bend + Math.max(0, -pa - 20 * deg) * 0.5
    const angle = lerp(WAVE.angle, pa, blend) + wobble
    const length = lerp(WAVE.length, POINT.length, blend) + reach
    const bend = lerp(WAVE.bend, pointBend, blend)
    const ctrl = lerp(WAVE.ctrl, POINT.ctrl, blend)
    const sy = lerp(WAVE.shoulderY, POINT.shoulderY, blend)
    const ex = SHOULDER_X + Math.cos(angle) * length
    const ey = sy + Math.sin(angle) * length
    const cx = SHOULDER_X + Math.cos(angle + bend) * length * ctrl
    const cy = sy + Math.sin(angle + bend) * length * ctrl
    arm.setAttribute('d', `M${SHOULDER_X} ${sy} Q${cx.toFixed(1)} ${cy.toFixed(1)} ${ex.toFixed(1)} ${ey.toFixed(1)}`)
    hand.setAttribute('cx', (ex + Math.cos(angle) * 6).toFixed(1))
    hand.setAttribute('cy', (ey + Math.sin(angle) * 6).toFixed(1))
    hand.setAttribute('r', lerp(WAVE.hand, POINT.hand, blend).toFixed(1))

    EYES.forEach((eye, i) => {
      const el = eyeRefs.current[i]
      if (!el) return
      const w = lerp(eye.wave.w, eye.point.w, blend)
      const h = lerp(eye.wave.h, eye.point.h, blend)
      el.setAttribute('x', (-w / 2).toFixed(1))
      el.setAttribute('y', (-h / 2).toFixed(1))
      el.setAttribute('width', w.toFixed(1))
      el.setAttribute('height', h.toFixed(1))
      el.setAttribute('rx', (w / 2).toFixed(1))
      el.setAttribute(
        'transform',
        `translate(${lerp(eye.wave.x, eye.point.x, blend).toFixed(1)} ${lerp(eye.wave.y, eye.point.y, blend).toFixed(1)}) rotate(${lerp(eye.wave.r, eye.point.r, blend).toFixed(1)})`,
      )
    })
  }

  useAnimationFrame(time => {
    if (time - lastMeasure.current > 500) {
      lastMeasure.current = time
      measure()
    }
    if (reduceMotion) {
      draw(1, 0, 0)
      return
    }
    const t = (time / 1000) % CYCLE
    let blend: number
    let wobble = 0
    let reach = 0
    if (t < T_WAVE) {
      blend = 0
      // With the buttons above him (phones) the wave and the point are both
      // arm-up, so a quick wave reads as flapping; there he waves slower and
      // narrower. 1.6 Hz is two-and-a-half full waves in the 2.4 s; 0.83 Hz,
      // two.
      const above = pointAngle.current < -45 * deg
      wobble = (above ? 0.18 : 0.26) * Math.sin(t * 2 * Math.PI * (above ? 0.83 : 1.6))
    } else if (t < T_WAVE + T_REACH) {
      blend = ease((t - T_WAVE) / T_REACH)
    } else if (t < T_WAVE + T_REACH + T_HOLD) {
      blend = 1
      // A small, slow jab towards the buttons while holding.
      reach = 8 * Math.max(0, Math.sin((t - T_WAVE - T_REACH) * 2 * Math.PI * 0.6))
    } else {
      blend = 1 - ease((t - T_WAVE - T_REACH - T_HOLD) / T_REACH)
    }
    draw(blend, wobble, reach)
  })

  return (
    <svg
      ref={svgRef}
      viewBox="-320 -330 640 650"
      aria-hidden="true"
      // The light pose's #0000CC sinks into the dark canvas; the dark set
      // uses #5A5AFF, so the colour comes from currentColor.
      className={`-scale-x-100 text-[#0000CC] dark:text-[#5A5AFF] ${className ?? ''}`}
    >
      <ellipse cx="0" cy="268" rx="118" ry="14" className="fill-[#141413] opacity-[0.08] dark:fill-white dark:opacity-[0.06]" />
      <g fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="24">
        <path d="M-32 140 L-34 236" />
        <path d="M32 140 L34 236" />
        <path d="M-58 250 H-18" />
        <path d="M18 250 H58" />
        <path d="M-130 20 Q-218 48 -222 102" />
        <path ref={armRef} d="M130 0 Q224 -26 230 -84" />
      </g>
      <circle cx="-222" cy="106" r="20" fill="currentColor" />
      <circle ref={handRef} cx="230" cy="-92" r="22" fill="currentColor" />
      <rect x="-147" y="-147" width="294" height="294" rx="20" fill="currentColor" transform="translate(0 -10) scale(0.9 1.1) rotate(45)" />
      {/* Drawn in the waving shape; draw() morphs them every frame. */}
      <g fill="#F8F8F8">
        {EYES.map(({ wave: e }, i) => (
          <rect
            key={i}
            ref={el => { eyeRefs.current[i] = el }}
            x={-e.w / 2}
            y={-e.h / 2}
            width={e.w}
            height={e.h}
            rx={e.w / 2}
            transform={`translate(${e.x} ${e.y}) rotate(${e.r})`}
          />
        ))}
      </g>
    </svg>
  )
}
